import type { UseTuningSession } from '../hooks/useTuningSession';
import type { UseSetups } from '@/hooks/useSetups';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';
import { SpineCheck } from '@/components/SpineCheck';

interface ArrowPrepData {
  /** Spin-test result for the worst arrow in the set. */
  spinTestResult?: 'untested' | 'all-straight' | 'minor-wobble' | 'visible-wobble';
  /** Number of arrows that wobbled visibly (out of dozen). */
  wobbleCount?: number;
  /** Did the archer match-weight the arrows (sorted to within 2 gr)? */
  matchWeighted?: boolean;
  /** Did the archer index nocks (mark cock vane / preferred rotation)? */
  nocksIndexed?: boolean;
  /** Were inserts squared (faced) before installation? */
  insertsSquared?: boolean;
}

interface Props {
  tuning: UseTuningSession;
  /**
   * Optional setups handle. When provided, the step renders the live SpineCheck
   * panel for the active arrow + a "Switch to {suggested} spine" button that
   * writes through to useSetups. Without it (legacy callers), the spine panel
   * is omitted.
   */
  setups?: UseSetups;
}

/**
 * Step 1.5 — Arrow Prep & Spin-Test Gate
 *
 * Added in v114 after the School of Nock gap analysis. Dudley dedicates two
 * full episodes to arrow prep as the foundation EVERYTHING else sits on. A
 * single bent arrow can paper-tune perfectly for the OTHER eleven arrows in
 * a dozen and the archer will blame their rest, their cams, their grip, and
 * their broadheads before they check the shafts.
 *
 * Four checks:
 *   1. Spin-test all arrows — eyeball for wobble at the point + nock ends.
 *   2. Match-weight pairs — sort to ±2 gr within the dozen for tuned-team behavior.
 *   3. Index nocks — mark cock vane / preferred rotation so each arrow leaves
 *      the bow with the same dynamic spine vector.
 *   4. Square inserts — face inserts before glue-in so they sit perpendicular
 *      to the shaft axis; otherwise point-mounted broadheads cant on impact.
 *
 * This step gates the rest of the tune — if it fails, the archer gets steered
 * to fix the arrow set BEFORE wasting hours paper-tuning a bent shaft.
 */
export function ArrowPrepStep({ tuning, setups }: Props) {
  const data = (tuning.session.stepData['arrow_prep'] as ArrowPrepData | undefined) ?? {};
  const update = (patch: Partial<ArrowPrepData>) =>
    tuning.updateStepData<ArrowPrepData>('arrow_prep', { ...data, ...patch });

  /* Active arrow's BuildState — feeds the SpineCheck panel. The wizard is
   * bound to a Setup; the Setup has an arrows[] + activeArrowIdx. SpineCheck
   * needs a BuildState + a setField that writes through. */
  const activeArrow = setups?.activeSetup?.arrows[setups.activeSetup.activeArrowIdx] ?? null;

  const hasIssue =
    data.spinTestResult === 'visible-wobble' ||
    (data.wobbleCount !== undefined && data.wobbleCount > 0);

  return (
    <>
      <GoalCard title="The arrow gate — fix the shafts before you tune the bow">
        The #1 rule for an archer who can't dial in their groups: check the arrows
        first. A single bent shaft will paper-tune the bow for the other eleven
        arrows in your dozen — you'll chase phantom adjustments until you spin-test
        the set and find the culprit. This step is fast and saves hours.
      </GoalCard>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Spin-test</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          Spin each arrow on its point against a hard surface (or use a Pine Ridge /
          Apple spinner). Watch the nock end — a straight arrow rotates smoothly with
          zero visible wobble. A bent arrow shows side-to-side oscillation at the nock.
          Common causes: shipping damage, target-pile impacts, mid-bow brush on draw,
          old shafts after many bare-shaft / broadhead group sessions.
        </p>

        <Field label="What did the spin-test show?">
          <select
            value={data.spinTestResult ?? 'untested'}
            onChange={(e) =>
              update({ spinTestResult: e.target.value as ArrowPrepData['spinTestResult'] })
            }
            className={inputClass}
          >
            <option value="untested">Not tested yet</option>
            <option value="all-straight">Every arrow spins clean · no wobble</option>
            <option value="minor-wobble">A couple have minor wobble · still serviceable</option>
            <option value="visible-wobble">One or more wobble visibly · cull or re-fletch</option>
          </select>
        </Field>

        {data.spinTestResult === 'visible-wobble' && (
          <div className="mt-3 space-y-3">
            <Field label="How many arrows wobble (out of your dozen)?">
              <input
                type="number"
                min={0}
                max={24}
                value={data.wobbleCount ?? ''}
                onChange={(e) =>
                  update({ wobbleCount: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })
                }
                placeholder="e.g. 2"
                className={inputClass}
              />
            </Field>
            <Status>
              Pull those arrows from the tuning set. Continuing the wizard with bent
              shafts will give you false-positive tuning signals — you'll adjust the
              rest to fit the bent arrow, then the straight ones will tear opposite.
              Cull or re-fletch first.
            </Status>
          </div>
        )}

        {data.spinTestResult === 'minor-wobble' && (
          <div className="mt-3">
            <Status>
              Minor wobble at the nock can sometimes be re-indexed away (see Step 10 —
              Nock Tuning). For now, mark which arrows wobble and don't use them as
              your bare-shaft reference. Use a straight one for that.
            </Status>
          </div>
        )}

        {data.spinTestResult === 'all-straight' && (
          <div className="mt-3">
            <Status ok>
              Whole set passes. This is the right starting line for everything that
              follows.
            </Status>
          </div>
        )}
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Match-weight + nock-indexing + insert-squaring</h3>
        <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
          The "three boring checks" that separate a dozen that flies as a team from a
          dozen that flies as twelve individuals.
        </p>

        <div className="space-y-3">
          <Field label="Match-weighted within ±2 gr?">
            <select
              value={data.matchWeighted === undefined ? '' : data.matchWeighted ? 'yes' : 'no'}
              onChange={(e) =>
                update({
                  matchWeighted: e.target.value === '' ? undefined : e.target.value === 'yes',
                })
              }
              className={inputClass}
            >
              <option value="">Not specified</option>
              <option value="yes">Yes · weighed on a grain scale, within ±2 gr</option>
              <option value="no">No · using factory tolerance only</option>
            </select>
          </Field>
          {data.matchWeighted === false && (
            <Status>
              Weigh the dozen on a grain scale, sort into matched sets of three. Cost:
              5 minutes. Benefit: matched sets group ~30% tighter at 60+ yd. Factory
              tolerance is ±2 gr but real-world variance is often 4-6 gr.
            </Status>
          )}

          <Field label="Nocks indexed (cock vane marked)?">
            <select
              value={data.nocksIndexed === undefined ? '' : data.nocksIndexed ? 'yes' : 'no'}
              onChange={(e) =>
                update({
                  nocksIndexed: e.target.value === '' ? undefined : e.target.value === 'yes',
                })
              }
              className={inputClass}
            >
              <option value="">Not specified</option>
              <option value="yes">Yes · cock vane painted / labelled on each arrow</option>
              <option value="no">No · nocks rotate freely</option>
            </select>
          </Field>
          {data.nocksIndexed === false && (
            <Status>
              Paint a dot on the preferred cock vane of each arrow so you nock the
              same way every shot. Random nock rotation = each arrow leaves the bow
              with a different dynamic-spine vector = wider groups at distance. This
              is Step 10 (Nock Tuning) in our wizard — flag it for later.
            </Status>
          )}

          <Field label="Inserts squared (faced) before glue-in?">
            <select
              value={data.insertsSquared === undefined ? '' : data.insertsSquared ? 'yes' : 'no'}
              onChange={(e) =>
                update({
                  insertsSquared: e.target.value === '' ? undefined : e.target.value === 'yes',
                })
              }
              className={inputClass}
            >
              <option value="">Not specified</option>
              <option value="yes">Yes · faced with a G5 / Firenock / Razor squaring tool</option>
              <option value="no">No · glued in factory-cut</option>
            </select>
          </Field>
          {data.insertsSquared === false && (
            <Status>
              Squaring the insert face removes the carbon-cut burr that tilts the point
              by 0.5-1°. That tilt becomes 1-2" of broadhead planing at 40 yd. Most arrow
              shops do this as part of cut-to-length — confirm with whoever cut yours.
            </Status>
          )}
        </div>
      </Card>

      {hasIssue && (
        <WarningCard title="Recommendation: fix arrows before continuing">
          You flagged arrow-side issues. Spend 20 minutes culling, re-fletching, or
          re-installing inserts before continuing to Bow Grip and the rest of the
          wizard. Tuning a bow around bent shafts is the most common reason archers
          come to a shop with "groups won't tighten."
        </WarningCard>
      )}

      {/* Live spine validation against the user's bow + arrow inputs. Renders
          only when a Setup with at least one arrow is bound — Wizard always has
          this. Calls into useSetups.updateActiveArrow when the user clicks
          "Switch to {suggested} spine" so the change persists into every
          downstream wizard step. */}
      {activeArrow && setups && (
        <SpineCheck
          state={activeArrow}
          setField={(field, value) => {
            setups.updateActiveArrow({ [field]: value });
          }}
        />
      )}

      <InfoCard title="Why this comes before the bow tune">
        Every bow-tuning step from here on uses arrow flight as the signal. If the
        signal is contaminated by a bent shaft, an unindexed nock, or a tilted insert,
        the tune dials in on the contamination instead of the bow. The principle is
        simple: if your arrows aren't good, your bow can't be.
      </InfoCard>

      <TipCard title="One-time investment worth $50">
        A grain scale (Pine Ridge Arrow Inspector — ~$30) and an arrow squaring tool
        (G5 ASD — ~$25) pay for themselves in saved tuning hours. They turn the
        invisible variables into measurable ones.
      </TipCard>
    </>
  );
}

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[11px] text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function Status({ children, ok }: { children: React.ReactNode; ok?: boolean }) {
  return (
    <div
      className={`text-sm leading-relaxed p-2.5 rounded border ${
        ok
          ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-300'
          : 'border-amber-700/40 bg-amber-950/20 text-amber-300'
      }`}
    >
      {children}
    </div>
  );
}
